import * as React from "react";
import { homeLink, request } from "./../../../../../services/cpln";
import { NGProviderBrowser } from "../../../ngProviderBrowser";
import { CardContent } from "../../../../../components/antd/card/CardContent";
import { CardTitle } from "../../../../../components/antd/card/CardTitle";
import { Button, Card, Space, Tooltip } from "antd";
import { Mk8sInput } from "../../../../../schema/types/mk8s/input/mk8s";
import { CaretRightOutlined, EllipsisOutlined, PlusOutlined } from "@ant-design/icons";
import { inputToMk8s } from "../../../draft/utils/inputToMk8s";
import { CardCollapsibleContent } from "../../../../../components/antd/card/CardCollapsibleContent";
import { ProviderPreInstallScript } from "../../../advanced/providerPreInstallScript";
import { ProviderAutoscaler } from "../../../advanced/providerAutoscaler";
import { v4 as uuidv4 } from "uuid";
import { formSetValue, formTriggerValidation, useCpFormValue } from "../../../../gvc/testNewForm/utils";
import { CpFormItem } from "../../../../gvc/testNewForm/CpFormItem";
import { CpLabel } from "../../../../gvc/testNewForm/CpLabel";
import { CpErrorWarning } from "../../../../gvc/testNewForm/CpErrorWarning";
import { CpInput } from "../../../../gvc/testNewForm/CpInput";
import { CpArrayString } from "../../../../gvc/testNewForm/CpArrayString";
import { store } from "../../../../../store";
import { cloneDeep } from "lodash";
import { useCpFormContext } from "../../../../../components/form/useCpFormContext";
import { CpErrorWarningArrayIcon } from "../../../../gvc/testNewForm/CpErrorWarningArrayIcon";
import { CpKindSelect } from "../../../../gvc/testNewForm/temp/CpKindSelect";
import { EmbeddedSecretCreate } from "../../../../secret/create/embedded";
import { useDrawerContext } from "../../../../../components/antd/drawerContext";
import { parseLink } from "../../../../../utils/linkParser/linkParser";
import { CpLabelTooltip } from "../../../../gvc/testNewForm/CpLabelTooltip";
import { CpTooltip } from "../../../../gvc/testNewForm/CpTooltip";

interface Props {}
export const OblivusProviderSelf: React.FC<Props> = ({}) => {
  const { id } = useCpFormContext();

  const drawer = useDrawerContext();

  const [browserKey, setBrowserKey] = React.useState("");
  const [isAdvancedCollapsed, setIsAdvancedCollapsed] = React.useState(true);

  const datacenter = useCpFormValue(`oblivus.datacenter`);
  const sshKeys = useCpFormValue(`oblivus.sshKeys`);

  return (
    <>
      <div className="cpln-form-section cpln-ant-card-container">
        <Card>
          <CardTitle>
            Oblivus Provider
            <CpErrorWarningArrayIcon
              names={[
                `oblivus.datacenter`,
                `oblivus.tokenSecretLink`,
                `oblivus.sshKeys`,
                `oblivus.autoscaler`,
                `oblivus.preInstallScript`,
              ]}
            />
          </CardTitle>
          <CardContent>
            <CpFormItem>
              <CpLabel name={`oblivus.datacenter`}>Datacenter</CpLabel>
              <Space.Compact>
                <CpInput name={`oblivus.datacenter`} style={{ width: 600 }} />
                <Tooltip title={`Browse`}>
                  <Button icon={<EllipsisOutlined />} onClick={() => setBrowserKey("locations")} />
                </Tooltip>
              </Space.Compact>
              <CpErrorWarning name={`oblivus.datacenter`} />
            </CpFormItem>
            <CpFormItem>
              <CpLabelTooltip>
                <CpLabel name={`oblivus.tokenSecretLink`}>Oblivus Token Secret</CpLabel>
                <CpTooltip
                  tooltipProps={{
                    title: `A dictionary secret that contains your Oblivus key and token, it will be used to manage servers in your Oblivus account`,
                  }}
                />
              </CpLabelTooltip>
              <Space.Compact>
                <CpKindSelect
                  kind="secret"
                  name={`oblivus.tokenSecretLink`}
                  optionValue="name"
                  queries={[{ property: "type", value: "dictionary" }]}
                  style={{ width: 600 }}
                />
                <Tooltip title={`Create an Opaque Secret`}>
                  <Button
                    onClick={() => {
                      drawer.setPersistent(
                        `mk8s-oblivus-create-secret`,
                        "Create Dictionary Secret",
                        <>
                          <EmbeddedSecretCreate
                            onCancel={() => drawer.closePersistent()}
                            onDone={(itemLink) => {
                              drawer.closePersistent();
                              const { name } = parseLink(itemLink, { useInputCtx: true });
                              const secretState = {
                                value: name,
                                name: name,
                                link: itemLink,
                              };
                              formSetValue(id, `oblivus.tokenSecretLink`, secretState);
                              formTriggerValidation(id);
                            }}
                            lockedType={`dictionary`}
                          />
                        </>,
                        600,
                      );
                    }}
                    icon={<PlusOutlined />}
                  />
                </Tooltip>
              </Space.Compact>
              <CpErrorWarning name={`oblivus.tokenSecretLink`} />
            </CpFormItem>
            <div style={{ maxWidth: 600 }}>
              <CpArrayString label={`SSH Keys`} name={`oblivus.sshKeys`} />
            </div>
          </CardContent>
          <div className="mt-4">
            <CardTitle style={{ marginBottom: 0, fontSize: 20 }}>
              <div className={`flex items-baseline ${isAdvancedCollapsed ? "" : "mb-4"}`}>
                <div
                  className={`flex items-baseline gap-2 color-link-hover cursor-pointer`}
                  onClick={() => {
                    setIsAdvancedCollapsed((x) => !x);
                  }}
                >
                  <CaretRightOutlined rotate={isAdvancedCollapsed ? 0 : 90} className={`self-center`} />
                  <span>Advanced</span>
                </div>
              </div>
            </CardTitle>
          </div>
          <CardCollapsibleContent collapsed={isAdvancedCollapsed}>
            <ProviderPreInstallScript provider="oblivus" />
            <ProviderAutoscaler provider="oblivus" />
          </CardCollapsibleContent>
        </Card>
      </div>
      {browserKey === "locations" ? (
        <NGProviderBrowser
          title="Browse Oblivus Data Centers"
          initialValue={datacenter}
          fetchData={async () => {
            const form = store.getState().form.instances[id];
            const values: Mk8sInput = form.values as any;

            const { data } = await request({
              service: "api",
              url: homeLink("mk8s") + "/-discover",
              method: "post",
              body: {
                objectType: "locations",
                cluster: inputToMk8s(values),
              },
            });
            return data.data;
          }}
          onOk={(value) => {
            formSetValue(id, `oblivus.datacenter`, value as string);
            formTriggerValidation(id);
          }}
          onClose={() => setBrowserKey("")}
          labels={["Name", "Description"]}
          getData={(item: any) => [item.name, item.name, item.description]}
        />
      ) : null}
      {browserKey === "ssh-keys" ? (
        <NGProviderBrowser
          title="Browse Oblivus SSH Keys"
          initialValue={sshKeys.map((i) => i.value)}
          fetchData={async () => {
            const form = store.getState().form.instances[id];
            const values: Mk8sInput = form.values as any;

            const { data } = await request({
              service: "api",
              url: homeLink("mk8s") + "/-discover",
              method: "post",
              body: {
                objectType: "ssh-keys",
                cluster: inputToMk8s(values),
              },
            });
            return data.data;
          }}
          multipleChoice
          onOk={(_value) => {
            const values: string[] = _value as any;
            const form = store.getState().form.instances[id];
            const formValues: Mk8sInput = form.values as any;

            const _sshKeys = cloneDeep(formValues.oblivus.sshKeys);
            for (const value of values) {
              if (_sshKeys.some((i) => i.value === value)) {
                continue;
              }
              _sshKeys.push({ id: uuidv4(), value: value });
              formSetValue(id, `oblivus.sshKeys`, _sshKeys);
            }
            formTriggerValidation(id);
          }}
          onClose={() => setBrowserKey("")}
          labels={["Name"]}
          getData={(item: any) => [String(item.name), item.name]}
        />
      ) : null}
    </>
  );
};
